import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiSearch, FiEye, FiChevronDown, FiFilter, FiPlus, FiRefreshCw } from 'react-icons/fi';
const BenefitList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const refreshData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        process.env.REACT_APP_API_PASS_LIST,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        }
      );

      const apiData = response.data?.data?.passes || [];

      const mappedBenefits = apiData.map(item => ({
        id: item.id,
        name: item.name,
        price: item.basePrice?.toLocaleString(),
        factor: item.factor?.toString(),
        amountFactorIncrease: item.amountFactorIncrement?.toString(),
        maxAmount: item.maxPrice?.toLocaleString(),
        duration: `${item.validityInDays} days`,
        userType: item.userTypes || [],
        discount: `${item.discountPercent}%`,
        maxDiscountAmount: item.maxDiscountAmount?.toString(),
        strikethroughPrice: item.strikeThroughPrice?.toString()
      }));

      setBenefits(mappedBenefits);
    } catch (error) {
      console.error('Failed to fetch benefits:', error);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);


  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);
  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const filtered = benefits.filter((b) =>
    Object.values(b)
      .flatMap(v => Array.isArray(v) ? v : [v])
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );


  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const totalPages = Math.ceil(filtered.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };
  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  const sortedBenefits = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (!aValue || !bValue) return 0;


    if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
      aValue = parseFloat(aValue);
      bValue = parseFloat(bValue);
    } else {
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };
  const currentRows = sortedBenefits.slice(indexOfFirstRow, indexOfLastRow);

  const totalEntries = filtered.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  const handleEdit = (id, event) => {
    const url = `/createPass?Passid=${id}`;
    if (event?.metaKey || event?.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url); 
    }
  };


  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete this item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yep, trash it',
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_DELETE_PASS}?pass_id=${id}`,
            {
              headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
              }
            }
          );

          if (response.data.success) {
            setBenefits((prev) => prev.filter((b) => b.id !== id));

            Swal.fire({
              icon: 'success',
              toast: true,
              position: 'top-end',
              timer: 1500,
              showConfirmButton: false,
              title: 'Deleted Successfully'
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Failed!',
              text: response.data.message || 'Unable to delete the item.',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000
            });
          }
        } catch (error) {
          console.error('API Delete Error:', error);
          Swal.fire({
            icon: 'error',
            title: 'Oops!',
            text: error.response?.data?.message || 'Something went wrong while deleting.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
        }
      }
    });
  };

  const handleNewBenefit = (event) => {
    const url = `/createPass`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  return (
    <div className="enrollment-container">

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>

        <button
          className="create-button-new"
          onClick={(e) => handleNewBenefit(e)}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Create Pass
          </span>
        </button>
      </div>
      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading">
            <div className="tt-loading-spinner"></div>
            <p>Loading Pass...</p>
          </div>
        ) : (
          <>
            {currentRows.length > 0 ? (
              <table className="tt-table">
                <thead>
                  <tr>
                    {[
                      { label: 'Name', key: 'name' },
                      { label: 'Price', key: 'price' },
                      { label: 'Factor', key: 'factor' },
                      { label: 'Amt. Factor', key: 'amountFactorIncrease' },
                      { label: 'Max Amount', key: 'maxAmount' },
                      { label: 'Duration', key: 'duration' },
                      { label: 'User Type', key: 'userType' },
                      { label: 'Discount', key: 'discount' },
                      { label: 'Max Discount', key: 'maxDiscountAmount' },
                      { label: 'Strikethrough Price', key: 'strikethroughPrice' },
                    ].map(({ label, key }) => (
                      <th key={key} onClick={() => handleSort(key)}>
                        <div className="th-content">
                          <span>{label}</span>
                          <FiChevronDown
                            className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                          />
                        </div>
                      </th>
                    ))}
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {currentRows.map((b) => (
                    <tr key={b.id}>
                      <td>{b.name}</td>
                      <td>{b.price}</td>
                      <td>{b.factor}</td>
                      <td>{b.amountFactorIncrease}</td>
                      <td>{b.maxAmount}</td>
                      <td>{b.duration}</td>
                      <td>{b.userType.join(', ')}</td>
                      <td>{b.discount}</td>
                      <td>{b.maxDiscountAmount}</td>
                      <td>{b.strikethroughPrice}</td>
                      <td className="action-buttons" style={{ borderBottom: 'none' }}>
                        <button className="edit-btn tooltip-btn" onClick={(e) => handleEdit(b.id, e)} title="Edit">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                          </svg>
                          <span className="tooltip-text">Edit</span>
                        </button>
                        <button className="delete-btn tooltip-btn" onClick={() => handleDelete(b.id)} title="Delete">
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                          </svg>
                          <span className="tooltip-text">Delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="tt-empty-state">
                <p>No Pass found</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default BenefitList;
